import { useQuery } from "react-query";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import { OlapScoresResultSchema } from "../olapScores/olapScoresSchema";

const TransportScorePostSchema = z.object({
    id: z.number(),
    olapScores: OlapScoresResultSchema,
});

export const getTransportPostKey = "getTransportPost" as const;

type TransportPostKind = "leg" | "circuit";

type Options = {
    kind: TransportPostKind;
    transportId: number | undefined;
    campaignId?: number;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
};

export const useGetTransportPost = (options: Options) => {
    const { kind, transportId, campaignId, enabled = true, scoresQuery } = options;
    const isEnabled = enabled && transportId !== undefined && campaignId !== undefined;

    const query = useQuery({
        queryKey: [getTransportPostKey, kind, transportId, campaignId, scoresQuery],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: () => fetchTransportPost(kind, transportId, campaignId, scoresQuery),
        select: (response) => {
            if (!response) {
                return;
            }

            const post = response.data;
            return zodParseWithLog(post, TransportScorePostSchema, getTransportPostKey);
        },
    });

    return {
        post: query.data,
        ...query,
    };
};

export const fetchTransportPost = async (
    kind: TransportPostKind,
    transportId: number | undefined,
    campaignId: number | undefined,
    scoresQuery?: OlapScoresQuery,
) => {
    const url =
        kind === "leg" ?
            `/api/transport-legs/${transportId}/ssq-post`
        :   `/api/transport-circuits/${transportId}/ssq-post`;

    return await loggedInApiCall.get(url, {
        headers: {
            Accept: "application/json",
        },
        params: {
            campaign: campaignId ?? undefined,
            ...scoresQuery,
        },
    });
};
